/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChannelsGrid, Spinner } from 'ui/components';
import { Channel, Ducks, EntryPoint, MenuUiArea, StoreState, TimeUtils } from '@wit/vtv-x-foundation-lib';
import { WatchTvSection, WatchTvWrapperProps } from 'types/components/WatchTv';
import {
  Selectors as FilterChannelsSelectors,
  ActionCreators as FilterChannelsCreators,
} from 'store/ducks/watchTv/filterChannels';
import { ChannelFilter } from 'store/ducks/watchTv/channelFilters';
import WatchTvBar from './WatchTvBar';
import TVGuideGrid from '../TvGuide/TVGuideGrid/TVGuideGrid';
import { useRouter } from '../../../hooks/useRouter';

function WatchTvWrapper({ overlay, setShowWrapper }: WatchTvWrapperProps): JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();

  const channels = useSelector(FilterChannelsSelectors.getFilteredChannels);
  const channelFilterProcessing = useSelector(FilterChannelsSelectors.isChannelFilterProcessing);
  const menuItem = useSelector((store: StoreState) => Ducks.Menu.Selectors.getMainMenuItem(store, MenuUiArea.EPG));
  const menuItemId = menuItem?.id;

  const [section, setSection] = useState<WatchTvSection>(WatchTvSection.ON_NOW);
  const [filter, setFilter] = useState<ChannelFilter | undefined>();
  const [selectedDay, setSelectedDay] = useState(TimeUtils.now());

  const navigateToWatchTvChannel = useCallback(
    (channel: Channel) => {
      router.push({
        asset: channel,
        state: { forceAutoPlay: overlay || undefined },
        entryPoint: EntryPoint.CLU,
        menuItemId,
      });
    },
    [menuItemId, overlay, router]
  );

  const renderSection = () => {
    if (section === WatchTvSection.ON_NOW) {
      return channels && !channelFilterProcessing ? (
        <ChannelsGrid data={channels} onChannelClick={navigateToWatchTvChannel} />
      ) : (
        <Spinner isActive={channelFilterProcessing} />
      );
    }
    if (section === WatchTvSection.TV_GUIDE) {
      return channels && !channelFilterProcessing ? (
        <TVGuideGrid
          channels={channels}
          day={selectedDay}
          key={selectedDay}
          setSelectedDay={setSelectedDay}
          menuItemId={menuItemId}
        />
      ) : (
        <Spinner isActive={channelFilterProcessing} />
      );
    }
    return null;
  };

  useEffect(() => {
    if (filter) {
      dispatch(FilterChannelsCreators.filterChannels(filter));
    }
  }, [dispatch, filter]);

  useEffect(() => {
    if (overlay) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [overlay]);

  useEffect(() => {
    if (section === WatchTvSection.TV_GUIDE) {
      setSelectedDay(TimeUtils.now());
    }
  }, [section]);

  return (
    <div className={`watchtv-wrapper content-container tv-guide-background ${overlay ? 'overlay' : ''}`}>
      <WatchTvBar
        setFilter={setFilter}
        section={section}
        setSection={setSection}
        overlay={overlay}
        setShowWrapper={setShowWrapper}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
      />
      {renderSection()}
    </div>
  );
}

export default React.memo(WatchTvWrapper);
